import { Card, Col, Form, FormInstance, Row } from "antd";
import debounce from "lodash.debounce";
import { FC, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import FakeFancySubmitButton from "../../../components/fakeFancySubmitButton";
import FancySubmitButton from "../../../components/fancySubmitButton";
import DividerLine from "../../../components/lineSplit";
import LoadingContainer from "../../../components/loadingContainer";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import acceptTerms from "../../../components/modal/acceptTerms";
import UserStore from "../../../contexts/userStore";
import _t from "../../../lang/translate";
import PageBody from "../../../layout/pageBody";
import PageTitle from "../../../layout/pageTitle";
import { getPath } from "../../../routes/appRoutes";
import { getProductPrices } from "../../../services/authService";
import { scrapeLink } from "../../../services/miscService";
import { saveTaxCase } from "../../../services/taxService";
import { getRemoteData } from "../../../services/vehicleService";
import { RemoteVehicleData, ScrapedVehicleType, ScreenProps } from "../../../types/appTypes";
import { isTaxDataSource, TaxCase, TaxCaseCreation, TaxDataSourceType } from "../../../types/taxTypes";
import consts from "../../../utilities/consts";
import { convertToApiFileList } from "../../../utilities/convertTypes";
import { isValidLink } from "../../../utilities/readLink";
import { isValidVin } from "../../../utilities/stringExtraFunctions";
import { isAxiosError } from "../../../utilities/typeGuard";
import TaxNewCaseAdminPanel from "../taxCommon/admin/taxNewCaseAdminPanel";
import { latestVersion } from "./calc/taxCalcService";
import TaxFetchCarFC from "./TaxFetchCarFC";

const TaxNewCase: FC<ScreenProps> = ({ title }) => {
  const { user, hasPermission, hasEnoughCredits } = UserStore.useContainer();
  const initialTaxNewCase = {
    id: 0,
    version: latestVersion,
    plan: 1,
    evaluationTypeId: 1,
    vehicle: {
      typeId: 1,
      link: "",
      vin: "",
    },
    note: null,
    files: null,
    calculationTypeId: 1,
    paymentMethodId: 1,
    client: {
      userId: user?.id,
      dealerId: user?.dealer?.id,
      typeId: 1,
    },
  };
  const isAdmin = hasPermission("tax.isAdmin");
  const [activeTab, setActiveTab] = useState<"auto" | "manual">("auto");
  const [newTaxCaseForm] = Form.useForm<TaxCaseCreation>();
  const [loading, setLoading] = useState<boolean>(false);
  const [clipCost, setClipCost] = useState<number>(1);
  const [vinVehicle, setVinVehicle] = useState<RemoteVehicleData | null>(null)
  const [linkVehicle, setLinkVehicle] = useState<ScrapedVehicleType | null>(null)
  const [firstRegDateDisabled, setFirstRegDateDisabled] = useState(true)
  const [mileageDisabled, setMileageDisabled] = useState(true)
  const [loadingVin, setLoadingVin] = useState(false)
  const [loadingLink, setLoadingLink] = useState(false)

  let history = useHistory();

  function validateAutoTabFields(values: TaxCaseCreation): void | never {
    if (activeTab === 'manual') return;
    let hasVin = isValidVin(values.vehicle?.vin);
    let hasLink = isValidLink(values.vehicle?.link);
    let errors: Parameters<FormInstance['setFields']>[0] = []
    if (hasVin && !hasLink) {
      if (!values.vehicle?.firstRegDate) {
        errors.push(
          {
            name: ['vehicle', 'firstRegDate'],
            errors: ['1. Reg Dato er påkrævet'],
          }
        )
      }
      if (!values.vehicle?.mileage) {
        errors.push(
          {
            name: ['vehicle', 'mileage'],
            errors: ['Kilometertal er påkrævet'],
          }
        )
      }
    }
    if (!hasLink && !hasVin) {
      errors.push(
        {
          name: ['vehicle', 'vin'],
          errors: ['Stelnummer eller link er påkrævet'],
        },
        {
          name: ['vehicle', 'link'],
          errors: ['Stelnummer eller link er påkrævet'],
        }

      )
    }

    if (errors.length) {
      newTaxCaseForm.setFields(errors)
      const message = errors?.map(e => e?.errors?.[0]).join(", ")
      throw new Error(message)
    }
  }

  const createNewTaxCase = async () => {
    const values = newTaxCaseForm.getFieldsValue()
    try {
      validateAutoTabFields(values)
      setLoading(true);
      await acceptTerms(user)

      let dataSourceId = 0;
      if (activeTab === 'auto' && isValidVin(values.vehicle?.vin)) {
        dataSourceId = 2;
      } else if (activeTab === 'auto' && isValidLink(values.vehicle?.link)) {
        dataSourceId = 1;
      } else if (activeTab === "manual") {
        dataSourceId = 3;
      }
      const { data: newTaxCase } = await saveTaxCase({
        ...values,
        dataSourceId,
        files: convertToApiFileList(values?.files || []),
      });
      redirectToCase(newTaxCase);
    } catch (error) {
      if (isAxiosError(error)) {
        const errors = error.response?.data?.errors;
        if (errors) {
          newTaxCaseForm.setFields(errors);
        }
      }
      renderErrorMessage(error)
      setLoading(false);
    }
  };
  const redirectToCase = (newTaxCase: TaxCase) => {
    history.replace(getPath("tax"))
  }

  const calculateEvaluationCost = () => {
    const isYellowToWhite = newTaxCaseForm.getFieldValue('evaluationTypeId') === 3;
    setClipCost((isYellowToWhite ?
      getProductPrices()?.taxYellowToWhite :
      getProductPrices()?.tax
    ) || 2);
  }

  const canPurchase = hasEnoughCredits(clipCost);
  const usageText = _t("when_click_calculate_you_use", "credit_from_your_account", ` ${canPurchase.usage.total} `);

  const handleFormChange = (changedValues: Partial<TaxCaseCreation>, values: TaxCaseCreation) => {
    if (changedValues.vehicle?.vin) {
      debouncedFetchVin(changedValues.vehicle.vin)
    }
    if (changedValues.vehicle?.link) {
      debouncedFetchLink(changedValues.vehicle.link)
    }
    const vehicleKey = changedValues.vehicle ? Object.keys(changedValues?.vehicle)[0] : null
    if (vehicleKey) {
      newTaxCaseForm.setFields([
        {
          name: ["vehicle", vehicleKey],
          errors: undefined
        }
      ])
    }

  }

  const fetchVinData = async (vin: string) => {
    try {
      if (!isValidVin(vin)) return;
      setLoadingVin(true)
      const { data } = await getRemoteData({ items: ['dmr'], vin: vin });
      setVinVehicle(data.data.dmr ? data.data : null)
      const firstRegDate = data.data?.dmr?.mapped?.firstRegDate ?? linkVehicle?.vehicle?.firstRegDate;


      setMileageDisabled(false)
      setFirstRegDateDisabled(!!firstRegDate)
      newTaxCaseForm.setFieldsValue({
        vehicle: {
          firstRegDate: firstRegDate,
        }
      })
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingVin(false)
    }
  }

  const debouncedFetchVin = useCallback(
    debounce((vin: string) => {
      fetchVinData(vin)
    }, 200), [fetchVinData]
  );

  const fetchLinkData = async (link: string) => {
    try {
      if (!isValidLink(link)) return;
      setLoadingLink(true)
      const { data } = await scrapeLink(link);
      setLinkVehicle(data.data)

      const mileage = data?.data?.vehicle?.mileage
      const firstRegDate = vinVehicle?.dmr?.mapped?.firstRegDate ?? data?.data?.vehicle?.firstRegDate

      setMileageDisabled(!!mileage)
      setFirstRegDateDisabled(!!firstRegDate)
      newTaxCaseForm.setFieldsValue({
        vehicle: {
          firstRegDate: firstRegDate,
          mileage: mileage
        }
      })

    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingLink(false)
    }
  }

  const debouncedFetchLink = useCallback(
    debounce((link: string) => {
      fetchLinkData(link)
    }, 200), [fetchLinkData]
  );


  return (
    <>
      <PageTitle fluid backBtn title={title} />

      <PageBody>

        <Form
          {...consts.formItemProps}
          form={newTaxCaseForm}
          initialValues={initialTaxNewCase}
          onFieldsChange={calculateEvaluationCost}
          onValuesChange={handleFormChange}
          requiredMark={false}
          onFinish={createNewTaxCase}
        >
          <Row align="top" gutter={24}>
            {isAdmin && (
              <Col {...consts.newCaseAdminColumnProps}>
                <TaxNewCaseAdminPanel loading={loading} />
              </Col>
            )}
            <Col {...consts.newCaseContentColumnProps(isAdmin)}>
              <Card bordered={false} className="card-tabs" title={_t("create") + " " + _t("tax_case")}>
                <LoadingContainer loading={loading}>
                  <TaxFetchCarFC
                    isAdmin={isAdmin}
                    onTabChange={setActiveTab}
                    activeTab={activeTab}
                    vinVehicle={vinVehicle}
                    linkVehicle={linkVehicle}
                    firstRegDateDisabled={firstRegDateDisabled}
                    mileageDisabled={mileageDisabled}
                    setFirstRegDateDisabled={setFirstRegDateDisabled}
                    setMileageDisabled={setMileageDisabled}
                    loadingLink={loadingLink}
                    loadingVin={loadingVin}
                  />
                  {!isAdmin && (
                    <div className="fancy-btn-wp text-center pt-1">
                      <DividerLine>
                        <small>
                          {!isAdmin && usageText}
                        </small>
                      </DividerLine>
                      {isAdmin || canPurchase.ok ? (
                        <FancySubmitButton loading={loading} />
                      ) : (
                        <FakeFancySubmitButton />
                      )}
                    </div>
                  )}
                </LoadingContainer>
              </Card>
            </Col>
          </Row>
        </Form>
      </PageBody>
    </>
  );
};

export default TaxNewCase;
